/**
 * Generate Audio pre-submit readiness — text, voice template binding, provider, runtime.
 * AUTO skill mode never blocks; hidden optional TTS params never block.
 */

import type { WorkspaceProviderOption } from '../../../api'
import {
  findProviderOptionById,
  isExplicitProviderSelectionInvalid,
  isProviderOptionSelectable,
  normalizeProviderSelectionMode,
  resolveCapabilityProviderOptionId,
} from './capabilityProviderSelection'
import {
  buildGenerationReadiness,
  mergeGenerationReadiness,
  type GenerationReadiness,
} from './generationReadiness'
import { classifyPersistedGenerateAudioError } from './generateAudioError'

export type GenerateAudioConnectedVoice = {
  templateId: string
  label: string
  providerVoiceId: string | null
  ready: boolean
  executable?: boolean
} | null

export type GenerateAudioConnectedText = {
  text: string
  sourceLabel: string
  ready: boolean
} | null

export function resolveGenerateAudioEffectiveText(args: {
  localText: string
  connectedText?: GenerateAudioConnectedText
}): string {
  if (args.connectedText?.ready && args.connectedText.text.trim()) {
    return args.connectedText.text.trim()
  }
  return args.localText.trim()
}

export function evaluateGenerateAudioTextReadiness(args: {
  localText: string
  connectedText?: GenerateAudioConnectedText
}): GenerationReadiness {
  const text = resolveGenerateAudioEffectiveText(args)
  if (!text) {
    return buildGenerationReadiness({
      blockingReasons: [
        {
          code: 'TEXT_MISSING',
          field: 'text',
          messageKey: 'generateAudio.readiness.textMissing',
          dimension: 'INPUT_READY',
        },
      ],
    })
  }
  return buildGenerationReadiness({})
}

export function evaluateGenerateAudioVoiceReadiness(args: {
  connectedVoice?: GenerateAudioConnectedVoice
  selectedVoiceTemplateId: string | null | undefined
  selectedSystemVoiceKey: string | null | undefined
}): GenerationReadiness {
  const hasVoice = Boolean(
    (args.connectedVoice?.ready && args.connectedVoice.templateId) ||
      args.selectedVoiceTemplateId ||
      args.selectedSystemVoiceKey,
  )
  if (!hasVoice) {
    return buildGenerationReadiness({
      blockingReasons: [
        {
          code: 'VOICE_TEMPLATE_MISSING',
          field: 'voice_template',
          messageKey: 'generateAudio.readiness.voiceTemplateMissing',
          dimension: 'INPUT_READY',
        },
      ],
    })
  }
  return buildGenerationReadiness({})
}

export function evaluateGenerateAudioVoiceExecutionReadiness(args: {
  connectedVoice?: GenerateAudioConnectedVoice
  selectedVoiceTemplateExecutable?: boolean | null
  selectedVoiceTemplateId: string | null | undefined
  selectedSystemVoiceKey: string | null | undefined
  persistedErrorMessage?: string | null
  persistedErrorCode?: string | null
}): GenerationReadiness {
  if (args.selectedSystemVoiceKey) return buildGenerationReadiness({})
  const connectedBlocked = Boolean(args.connectedVoice?.ready && args.connectedVoice.executable === false)
  const selectedBlocked =
    Boolean(args.selectedVoiceTemplateId) && args.selectedVoiceTemplateExecutable === false
  const inferredUnavailable = classifyPersistedGenerateAudioError({
    message: args.persistedErrorMessage,
    errorCode: args.persistedErrorCode,
  }).voiceBindingFailure
  if (connectedBlocked || selectedBlocked || inferredUnavailable) {
    return buildGenerationReadiness({
      blockingReasons: [
        {
          code: 'VOICE_TEMPLATE_UNAVAILABLE',
          field: 'voice_template',
          messageKey: 'generateAudio.readiness.voiceTemplateUnavailable',
          dimension: 'INPUT_READY',
        },
      ],
    })
  }
  return buildGenerationReadiness({})
}

export function evaluateGenerateAudioProviderReadiness(args: {
  fields: Record<string, unknown>
  providerOptions: WorkspaceProviderOption[]
  providerLoading: boolean
  providerError: boolean
  serverDefaultProviderOptionId?: string | null
}): GenerationReadiness {
  if (args.providerLoading) {
    return buildGenerationReadiness({
      blockingReasons: [
        {
          code: 'PROVIDER_LOADING',
          messageKey: 'generateAudio.readiness.providerLoading',
          dimension: 'PROVIDER_READY',
        },
      ],
    })
  }
  const hasLoadedOptions = args.providerOptions.length > 0
  if (!hasLoadedOptions && args.providerError) {
    return buildGenerationReadiness({
      blockingReasons: [
        {
          code: 'PROVIDER_LOAD_FAILED',
          messageKey: 'generateAudio.readiness.providerLoadFailed',
          dimension: 'PROVIDER_READY',
        },
      ],
    })
  }
  if (!hasLoadedOptions) {
    return buildGenerationReadiness({
      blockingReasons: [
        {
          code: 'NO_AVAILABLE_PROVIDER',
          messageKey: 'generateAudio.readiness.noAvailableProvider',
          dimension: 'PROVIDER_READY',
        },
      ],
    })
  }
  const anyAvailable = args.providerOptions.some(isProviderOptionSelectable)
  if (!anyAvailable) {
    return buildGenerationReadiness({
      blockingReasons: [
        {
          code: 'NO_AVAILABLE_PROVIDER',
          messageKey: 'generateAudio.readiness.noAvailableProvider',
          dimension: 'PROVIDER_READY',
        },
      ],
    })
  }
  const selectionMode = normalizeProviderSelectionMode(args.fields.providerSelectionMode)
  if (
    isExplicitProviderSelectionInvalid({
      selectionMode,
      storedProviderOptionId: (args.fields.providerOptionId as string | null) ?? null,
      options: args.providerOptions,
    })
  ) {
    return buildGenerationReadiness({
      blockingReasons: [
        {
          code: 'PROVIDER_UNAVAILABLE',
          messageKey: 'generateAudio.readiness.providerUnavailable',
          dimension: 'PROVIDER_READY',
        },
      ],
    })
  }
  const resolvedId = resolveCapabilityProviderOptionId({
    selectionMode,
    storedProviderOptionId: (args.fields.providerOptionId as string | null) ?? null,
    options: args.providerOptions,
    serverDefaultProviderOptionId: args.serverDefaultProviderOptionId,
  })
  const resolved = findProviderOptionById(args.providerOptions, resolvedId)
  if (!resolved || !isProviderOptionSelectable(resolved)) {
    return buildGenerationReadiness({
      blockingReasons: [
        {
          code: 'PROVIDER_UNAVAILABLE',
          messageKey: 'generateAudio.readiness.providerUnavailable',
          dimension: 'PROVIDER_READY',
        },
      ],
    })
  }
  return buildGenerationReadiness({})
}

export function evaluateGenerateAudioRuntimeReadiness(isBusy: boolean): GenerationReadiness {
  if (!isBusy) return buildGenerationReadiness({})
  return buildGenerationReadiness({
    blockingReasons: [
      {
        code: 'RUNTIME_BUSY',
        messageKey: 'generateAudio.readiness.runtimeBusy',
        dimension: 'RUNTIME_READY',
      },
    ],
  })
}

export function evaluateGenerateAudioReadiness(args: {
  fields: Record<string, unknown>
  localText: string
  connectedText?: GenerateAudioConnectedText
  connectedVoice?: GenerateAudioConnectedVoice
  selectedVoiceTemplateId: string | null | undefined
  selectedSystemVoiceKey: string | null | undefined
  selectedVoiceTemplateExecutable?: boolean | null
  persistedErrorMessage?: string | null
  persistedErrorCode?: string | null
  providerOptions: WorkspaceProviderOption[]
  providerLoading: boolean
  providerError: boolean
  serverDefaultProviderOptionId?: string | null
  isBusy: boolean
}): GenerationReadiness {
  return mergeGenerationReadiness(
    evaluateGenerateAudioTextReadiness({
      localText: args.localText,
      connectedText: args.connectedText,
    }),
    evaluateGenerateAudioVoiceReadiness({
      connectedVoice: args.connectedVoice,
      selectedVoiceTemplateId: args.selectedVoiceTemplateId,
      selectedSystemVoiceKey: args.selectedSystemVoiceKey,
    }),
    evaluateGenerateAudioVoiceExecutionReadiness({
      connectedVoice: args.connectedVoice,
      selectedVoiceTemplateExecutable: args.selectedVoiceTemplateExecutable,
      selectedVoiceTemplateId: args.selectedVoiceTemplateId,
      selectedSystemVoiceKey: args.selectedSystemVoiceKey,
      persistedErrorMessage: args.persistedErrorMessage,
      persistedErrorCode: args.persistedErrorCode,
    }),
    evaluateGenerateAudioProviderReadiness({
      fields: args.fields,
      providerOptions: args.providerOptions,
      providerLoading: args.providerLoading,
      providerError: args.providerError,
      serverDefaultProviderOptionId: args.serverDefaultProviderOptionId,
    }),
    evaluateGenerateAudioRuntimeReadiness(args.isBusy),
  )
}

/** Dev/test helper — first blocking reason code. */
export function generateAudioDisabledReasonCode(readiness: GenerationReadiness): string | null {
  return readiness.blockingReasons[0]?.code ?? null
}
